import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryClient';
import { toast } from 'sonner';
import i18n from '@/lib/i18n';

export interface Feedback {
  id: string;
  user_id: string;
  type: 'bug' | 'feedback' | 'praise';
  title: string;
  description: string | null;
  severity: string | null;
  page_url: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface SubmitFeedbackInput {
  type: 'bug' | 'feedback' | 'praise';
  title: string;
  description?: string;
  severity?: string;
  page_url?: string;
}

export function useUserFeedback() {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.feedback.byUser(user?.id ?? ''),
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('feedback')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as Feedback[];
    },
    enabled: !!user,
  });
}

export function useSubmitFeedback() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: SubmitFeedbackInput) => {
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('feedback')
        .insert({
          user_id: user.id,
          type: input.type,
          title: input.title,
          description: input.description || null,
          severity: input.type === 'bug' ? (input.severity || 'medium') : null,
          page_url: input.page_url || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.feedback.all });
      const messages = {
        bug: i18n.t('roadmap:toasts.bugSubmitted'),
        feedback: i18n.t('roadmap:toasts.feedbackSubmitted'),
        praise: i18n.t('roadmap:toasts.praiseSubmitted'),
      };
      toast.success(messages[variables.type]);
    },
    onError: () => {
      toast.error(i18n.t('roadmap:toasts.submitFailed'));
    },
  });
}
